/**
 * Settings Prompts Page
 * Feature: 015-custom-prompts, 017-instagram-reel-creation
 * Date: 2026-01-04
 *
 * Main page for managing custom post and reel generation prompts
 * Integrates PromptEditor and VariableHelp components
 * Handles loading custom prompt, saving, and showing feedback
 */

import { memo, useState, useReducer, useCallback } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { PromptEditorMemo } from '@/components/prompts/PromptEditor';
import { VariableHelpMemo } from '@/components/prompts/VariableHelp';
import { ResetConfirmDialogMemo } from '@/components/prompts/ResetConfirmDialog';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, RotateCcw, FileText, Film } from 'lucide-react';
import { cn } from '@/lib/utils';

interface IFeedbackMessage {
  type: 'success' | 'error';
  message: string;
}

type PromptType = 'post' | 'reel';

type PromptOperationState = {
  feedback: IFeedbackMessage | null;
  isSaving: boolean;
  showResetDialog: boolean;
  isResetting: boolean;
};

type PromptOperationAction =
  | { type: 'START_SAVE' }
  | { type: 'SAVE_SUCCESS'; payload: string }
  | { type: 'SAVE_ERROR'; payload: string }
  | { type: 'END_SAVE' }
  | { type: 'SHOW_RESET_DIALOG' }
  | { type: 'HIDE_RESET_DIALOG' }
  | { type: 'START_RESET' }
  | { type: 'RESET_SUCCESS'; payload: string }
  | { type: 'RESET_ERROR'; payload: string }
  | { type: 'END_RESET' }
  | { type: 'CLEAR_FEEDBACK' };

const initialOperationState: PromptOperationState = {
  feedback: null,
  isSaving: false,
  showResetDialog: false,
  isResetting: false,
};

function operationReducer(
  state: PromptOperationState,
  action: PromptOperationAction
): PromptOperationState {
  switch (action.type) {
    case 'START_SAVE':
      return { ...state, isSaving: true, feedback: null };
    case 'SAVE_SUCCESS':
      return { ...state, isSaving: false, feedback: { type: 'success', message: action.payload } };
    case 'SAVE_ERROR':
      return { ...state, isSaving: false, feedback: { type: 'error', message: action.payload } };
    case 'END_SAVE':
      return { ...state, isSaving: false };
    case 'SHOW_RESET_DIALOG':
      return { ...state, showResetDialog: true };
    case 'HIDE_RESET_DIALOG':
      return { ...state, showResetDialog: false };
    case 'START_RESET':
      return { ...state, isResetting: true, feedback: null };
    case 'RESET_SUCCESS':
      return {
        ...state,
        isResetting: false,
        showResetDialog: false,
        feedback: { type: 'success', message: action.payload },
      };
    case 'RESET_ERROR':
      return { ...state, isResetting: false, feedback: { type: 'error', message: action.payload } };
    case 'END_RESET':
      return { ...state, isResetting: false };
    case 'CLEAR_FEEDBACK':
      return { ...state, feedback: null };
  }
}

const SettingsPromptsPage = () => {
  const [activeTab, setActiveTab] = useState<PromptType>('post');
  // LOCAL_TYPE_JUSTIFIED: local-edit overlay keyed by PromptType — undefined means "use server value"
  const [localEdits, setLocalEdits] = useState<Partial<Record<PromptType, string>>>({});
  const [ops, dispatch] = useReducer(operationReducer, initialOperationState);

  // Query effective prompts by type
  const effectivePostPrompt = useQuery(api.userPrompts.getEffectivePromptByType, {
    promptType: 'post',
  });
  const effectiveReelPrompt = useQuery(api.userPrompts.getEffectivePromptByType, {
    promptType: 'reel',
  });

  // Query current custom prompts by type (to check if user has customized)
  const currentPostCustomPrompt = useQuery(api.userPrompts.getCurrentPromptByType, {
    promptType: 'post',
  });
  const currentReelCustomPrompt = useQuery(api.userPrompts.getCurrentPromptByType, {
    promptType: 'reel',
  });

  // Mutations
  const savePromptByType = useMutation(api.userPrompts.saveCustomPromptByType);
  const resetPromptByType = useMutation(api.userPrompts.resetPromptByType);

  // Derive current values during render — no effects needed
  const effectivePrompt = activeTab === 'post' ? effectivePostPrompt : effectiveReelPrompt;
  const currentCustomPrompt =
    activeTab === 'post' ? currentPostCustomPrompt : currentReelCustomPrompt;
  // Local edit takes priority; fall back to server value once loaded
  const promptText = localEdits[activeTab] ?? effectivePrompt ?? '';

  // Handle save
  const handleSave = useCallback(async () => {
    dispatch({ type: 'START_SAVE' });

    try {
      const result = await savePromptByType({
        promptType: activeTab,
        promptText,
      });

      if (result.success) {
        dispatch({
          type: 'SAVE_SUCCESS',
          payload: result.message || `Custom ${activeTab} prompt saved successfully!`,
        });
        setTimeout(() => dispatch({ type: 'CLEAR_FEEDBACK' }), 5000);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save custom prompt';
      dispatch({ type: 'SAVE_ERROR', payload: errorMessage });
    }
  }, [promptText, activeTab, savePromptByType]);

  // Handle prompt text change — stores edit locally keyed by tab
  const handlePromptChange = useCallback(
    (value: string) => {
      setLocalEdits(prev => ({ ...prev, [activeTab]: value }));
      dispatch({ type: 'CLEAR_FEEDBACK' });
    },
    [activeTab]
  );

  // Handle reset to default
  const handleResetClick = useCallback(() => {
    dispatch({ type: 'SHOW_RESET_DIALOG' });
  }, []);

  const handleResetConfirm = useCallback(async () => {
    dispatch({ type: 'START_RESET' });

    try {
      const result = await resetPromptByType({ promptType: activeTab });

      if (result.success) {
        dispatch({
          type: 'RESET_SUCCESS',
          payload:
            result.message ||
            `${activeTab === 'post' ? 'Post' : 'Reel'} prompt reset to default successfully!`,
        });
        setTimeout(() => dispatch({ type: 'CLEAR_FEEDBACK' }), 5000);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset prompt';
      dispatch({ type: 'RESET_ERROR', payload: errorMessage });
    }
  }, [activeTab, resetPromptByType]);

  const handleResetCancel = useCallback(() => {
    dispatch({ type: 'HIDE_RESET_DIALOG' });
  }, []);

  // Computed values
  const isLoading = effectivePrompt === undefined;
  const hasCustomPrompt = currentCustomPrompt !== null && currentCustomPrompt !== undefined;
  const showResetButton = hasCustomPrompt;

  // Tab configuration
  const tabs = [
    { id: 'post' as const, label: 'Post Prompts', icon: FileText },
    { id: 'reel' as const, label: 'Reel Prompts', icon: Film },
  ];

  return (
    <div className="container mx-auto max-w-7xl px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Custom AI Prompts</h1>
        <p className="mt-2 text-gray-600">
          Customize the AI prompt templates used for generating social media posts and Instagram
          reels. Use variables from your business profile to personalize the content.
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  dispatch({ type: 'CLEAR_FEEDBACK' });
                }}
                className={cn(
                  'flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-gray-900 text-gray-900'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mb-4 h-8 w-8 mx-auto animate-spin rounded-full border-4 border-gray-300 border-t-gray-900" />
            <p className="text-gray-600">Loading prompt settings...</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      {!isLoading && (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Left Column: Prompt Editor (2/3 width) */}
          <div className="lg:col-span-2">
            {/* Feedback Messages */}
            {ops.feedback && (
              <div className="mb-4">
                {ops.feedback.type === 'success' && (
                  <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 p-4 text-green-800">
                    <CheckCircle className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm font-medium">{ops.feedback.message}</span>
                  </div>
                )}
                {ops.feedback.type === 'error' && (
                  <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-red-800">
                    <XCircle className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm font-medium">{ops.feedback.message}</span>
                  </div>
                )}
              </div>
            )}

            {/* Info Box */}
            <div className="mb-6 rounded-md border border-blue-200 bg-blue-50 p-4">
              <h4 className="mb-2 text-sm font-semibold text-blue-900">
                About Custom {activeTab === 'post' ? 'Post' : 'Reel'} Prompts
              </h4>
              <ul className="space-y-1 text-sm text-blue-800">
                <li>
                  • Your custom prompt will be used for all future{' '}
                  {activeTab === 'post' ? 'post' : 'reel'} generations
                </li>
                <li>• Variables from your business profile will be automatically substituted</li>
                <li>• Prompts must be between 50 and 10,000 characters</li>
                <li>• Changes take effect immediately after saving</li>
                {activeTab === 'reel' && (
                  <li>
                    • Reel prompts should generate JSON with Hook, Scene_1, Scene_2, Scene_3, CTA,
                    Visual_Notes, Audio_Notes, and Hashtags
                  </li>
                )}
              </ul>
            </div>

            {/* Reset to Default Button */}
            {showResetButton && (
              <div className="mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Reset to System Default</h4>
                    <p className="mt-1 text-sm text-gray-600">
                      Remove your custom prompt and use the system default
                    </p>
                  </div>
                  <Button
                    onClick={handleResetClick}
                    variant="outline"
                    disabled={ops.isSaving || ops.isResetting}
                    className="ml-4"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Reset to Default
                  </Button>
                </div>
              </div>
            )}

            {/* Prompt Editor */}
            <PromptEditorMemo
              value={promptText}
              onChange={handlePromptChange}
              onSave={handleSave}
              isSaving={ops.isSaving}
            />
          </div>

          {/* Right Column: Variable Help (1/3 width) */}
          <div className="lg:col-span-1">
            <VariableHelpMemo />
          </div>
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      <ResetConfirmDialogMemo
        isOpen={ops.showResetDialog}
        onConfirm={handleResetConfirm}
        onCancel={handleResetCancel}
        isResetting={ops.isResetting}
      />
    </div>
  );
};

export const SettingsPromptsPageMemo = memo(SettingsPromptsPage);
